import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PlayCircle, ShieldAlert } from "lucide-react";
import {
    PARTICIPANTS,
    RUNTIME_CHAIN,
    RUNTIME_CHECKS,
    EXCEPTION_CHAIN,
    EXCEPTION_RECEIPT_FIELDS,
    FEEDBACK_STATS,
    STEWARD_RECEIVES,
    STEWARD_DOES_NOT_RECEIVE,
    RECEIPTS,
    CHANGE_ID,
} from "./data";
import { StatusChip, Kicker, DownloadReceiptBtn, ChainRow, LocalBoundary } from "./ui";

export const ActRuntime = ({ mode, onModeChange, view }) => {
    return (
        <section data-testid="act-runtime">
            <Kicker>Act 4 — Runtime execution and feedback</Kicker>
            <h2 className="mt-2 text-2xl font-medium tracking-tight text-slate-900">
                A synthetic instruction uses the newly activated field
            </h2>

            <div className="mt-4 inline-flex overflow-hidden rounded-sm border border-slate-200 bg-slate-200 gap-px" data-testid="runtime-mode-toggle">
                {[
                    ["success", "Governed execution (success)"],
                    ["invalid", "Legacy originator sends an unsupported value"],
                ].map(([k, label]) => (
                    <button
                        key={k}
                        onClick={() => onModeChange(k)}
                        data-testid={`runtime-mode-${k}`}
                        className={`px-4 py-2 text-sm transition-colors duration-150 ${
                            mode === k
                                ? "bg-blue-950 font-medium text-white"
                                : "bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {mode === "invalid" ? <ExceptionFlow /> : <SuccessFlow view={view} />}

            <FeedbackPanel />
        </section>
    );
};

function SuccessFlow({ view }) {
    const [tick, setTick] = useState(-1);
    const running = tick >= 0;
    const done = tick >= RUNTIME_CHAIN.length - 1;

    useEffect(() => {
        if (tick < 0 || tick >= RUNTIME_CHAIN.length - 1) return;
        const t = setTimeout(() => setTick((s) => s + 1), 700);
        return () => clearTimeout(t);
    }, [tick]);

    const viewFocus = view === "orig" ? "orig" : view === "recv" ? "recv" : null;

    return (
        <div className="mt-6" data-testid="runtime-success-flow">
            <div className="flex flex-wrap items-center gap-4">
                <button
                    onClick={() => setTick(0)}
                    disabled={running}
                    data-testid="run-instruction-btn"
                    className="inline-flex items-center gap-2 rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900 disabled:cursor-default disabled:opacity-50"
                >
                    <PlayCircle className="h-4 w-4" strokeWidth={1.5} />
                    Run synthetic instruction
                </button>
                <ChainRow steps={RUNTIME_CHAIN} activeIndex={tick} testIdPrefix="runtime-chain" />
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {PARTICIPANTS.map((p, i) => {
                    const c = RUNTIME_CHECKS[p.id];
                    const active = tick >= i + 1;
                    const dim = viewFocus && viewFocus !== p.id;
                    return (
                        <LocalBoundary
                            key={p.id}
                            highlight={running && tick === i + 1}
                            dim={dim}
                            testId={`runtime-node-${p.id}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <p className="text-sm font-medium text-slate-900">{p.short}</p>
                                <StatusChip tone={active ? "green" : "slate"} testId={`runtime-node-state-${p.id}`}>
                                    {active ? "PASSED" : "PENDING"}
                                </StatusChip>
                            </div>
                            <p className="mt-2 text-xs font-medium uppercase tracking-wide text-slate-400">{c.title}</p>
                            <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                                {c.rows.map((r) => (
                                    <li key={r} className={r.includes(":") ? "font-mono text-[11px]" : ""}>
                                        · {r}
                                    </li>
                                ))}
                            </ul>
                            {c.evidence && (
                                <>
                                    <p className="mt-3 text-xs font-medium uppercase tracking-wide text-slate-400">Evidence (references only)</p>
                                    <ul className="mt-1 space-y-0.5 text-xs text-slate-600">
                                        {c.evidence.map((r) => <li key={r}>· {r}</li>)}
                                    </ul>
                                </>
                            )}
                        </LocalBoundary>
                    );
                })}
            </div>

            {done && (
                <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 rounded-sm border border-slate-200 bg-white p-5"
                    data-testid="combined-receipt-panel"
                >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                                Combined network receipt
                            </p>
                            <p className="mt-1 text-sm text-slate-700">
                                Standards package {CHANGE_ID} v2 · usage profile v3.5 ·
                                each role-policy version · role decisions · evidence
                                references · exceptions (none) · timestamps · final
                                outcome SETTLED.
                            </p>
                        </div>
                        <DownloadReceiptBtn
                            json={RECEIPTS[3].json}
                            filename="runtime-decision-receipt.json"
                            testId="download-runtime-receipt"
                            label="Runtime decision receipt"
                        />
                    </div>
                </motion.div>
            )}
        </div>
    );
}

function ExceptionFlow() {
    const [blocked, setBlocked] = useState(false);
    const [repairTick, setRepairTick] = useState(-1);
    const released = repairTick >= EXCEPTION_CHAIN.length - 1;

    useEffect(() => {
        if (repairTick < 0 || repairTick >= EXCEPTION_CHAIN.length - 1) return;
        const t = setTimeout(() => setRepairTick((s) => s + 1), 650);
        return () => clearTimeout(t);
    }, [repairTick]);

    return (
        <div className="mt-6" data-testid="runtime-exception-flow">
            <div className="rounded-sm border border-slate-200 bg-white p-6">
                <p className="text-sm leading-relaxed text-slate-600">
                    A legacy originating channel sends{" "}
                    <span className="font-mono text-[13px] text-slate-900">
                        ExecutionDecisionMethod: AI_AUTOMATED
                    </span>{" "}
                    — this value is not in the approved code list.
                </p>

                {!blocked ? (
                    <button
                        onClick={() => setBlocked(true)}
                        data-testid="send-invalid-btn"
                        className="mt-4 inline-flex items-center gap-2 rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900"
                    >
                        <PlayCircle className="h-4 w-4" strokeWidth={1.5} />
                        Send legacy instruction
                    </button>
                ) : (
                    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-4">
                        <div
                            className="flex items-start gap-3 rounded-sm border border-red-200 bg-red-50 px-4 py-3"
                            data-testid="blocked-banner"
                        >
                            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-700" strokeWidth={1.5} />
                            <div>
                                <p className="font-mono text-xs font-medium uppercase tracking-wide text-red-800">
                                    BLOCKED — UNRECOGNISED STANDARD VALUE
                                </p>
                                <p className="mt-1 text-xs leading-relaxed text-red-900">
                                    The intermediary's local validation blocks the
                                    instruction. The configured usage profile and code-list
                                    checks determine the outcome.
                                </p>
                            </div>
                        </div>

                        <dl className="mt-4 grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2" data-testid="exception-receipt-fields">
                            {EXCEPTION_RECEIPT_FIELDS.map(([k, v]) => (
                                <div key={k}>
                                    <dt className="font-medium uppercase tracking-wide text-slate-400">{k}</dt>
                                    <dd className="mt-0.5 font-mono text-[11px] text-slate-700">{v}</dd>
                                </div>
                            ))}
                        </dl>

                        <div className="mt-5 flex flex-wrap items-center gap-4">
                            <button
                                onClick={() => setRepairTick(0)}
                                disabled={repairTick >= 0}
                                data-testid="correct-resubmit-btn"
                                className="inline-flex items-center gap-2 rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900 disabled:cursor-default disabled:opacity-50"
                            >
                                Correct field and resubmit
                            </button>
                            <ChainRow steps={EXCEPTION_CHAIN} activeIndex={repairTick} testIdPrefix="exception-chain" />
                        </div>

                        {released && (
                            <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="mt-4 text-sm text-green-800"
                                data-testid="exception-released-note"
                            >
                                The originating node corrected the field to{" "}
                                <span className="font-mono text-[13px]">AUTOMATED_WITHIN_MANDATE</span>{" "}
                                and the instruction was rechecked and released. The
                                exception receipt is returned upstream as bounded
                                evidence.
                            </motion.p>
                        )}
                    </motion.div>
                )}
            </div>
        </div>
    );
}

function FeedbackPanel() {
    const [returned, setReturned] = useState(false);

    return (
        <div className="mt-10" data-testid="feedback-panel">
            <Kicker>Upstream operational feedback</Kicker>
            <h3 className="mt-2 text-xl font-medium tracking-tight text-slate-900">
                After several simulated executions, bounded evidence returns to the steward
            </h3>

            <div className="mt-5 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
                <div className="rounded-sm border border-slate-200 bg-white p-6">
                    <div className="flex items-center justify-between gap-3">
                        <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                            Aggregated runtime feedback
                        </p>
                        <StatusChip tone="amber" testId="synthetic-data-label">
                            Synthetic demonstration data
                        </StatusChip>
                    </div>
                    <ul className="mt-4 space-y-2 text-sm text-slate-700" data-testid="feedback-stats">
                        {FEEDBACK_STATS.map((s) => (
                            <li key={s} className="flex gap-2">
                                <span className="text-slate-300">·</span>
                                {s}
                            </li>
                        ))}
                    </ul>

                    <div className="mt-6 flex flex-wrap items-center gap-3">
                        <button
                            onClick={() => setReturned(true)}
                            disabled={returned}
                            data-testid="return-feedback-btn"
                            className="inline-flex items-center gap-2 rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900 disabled:cursor-default disabled:opacity-50"
                        >
                            Send bounded feedback receipts to steward
                        </button>
                        <DownloadReceiptBtn
                            json={RECEIPTS[4].json}
                            filename="feedback-receipt.json"
                            testId="download-feedback-receipt"
                            label="Feedback receipt"
                        />
                    </div>

                    {returned && (
                        <motion.div
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-5 rounded-sm border border-green-200 bg-green-50 px-4 py-3"
                            data-testid="evidence-returned-banner"
                        >
                            <p className="font-mono text-xs font-medium uppercase tracking-wide text-green-800">
                                RUNTIME EVIDENCE RETURNED TO STEWARD
                            </p>
                            <p className="mt-1 text-xs leading-relaxed text-green-900">
                                Standards change no longer ends at publication. Runtime
                                evidence informs the next governed maintenance cycle.
                            </p>
                        </motion.div>
                    )}
                </div>

                <div className="rounded-sm border border-slate-200 bg-white p-6" data-testid="steward-bounds">
                    <div className="grid gap-5 sm:grid-cols-2">
                        <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-red-700">
                                The steward does not receive
                            </p>
                            <ul className="mt-2 space-y-1 text-xs text-slate-600">
                                {STEWARD_DOES_NOT_RECEIVE.map((s) => <li key={s}>· {s}</li>)}
                            </ul>
                        </div>
                        <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-green-700">
                                The steward receives
                            </p>
                            <ul className="mt-2 space-y-1 text-xs text-slate-600">
                                {STEWARD_RECEIVES.map((s) => <li key={s}>· {s}</li>)}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
